/**
 * The local pages (plan 2026_09_28_quies_search_estate.md, decisions 4 and 5): each written in one language for one
 * search intent, its owner query. None is a translation of another, so none names an hreflang alternate.
 *
 * Identity only — this module is small enough for the root bundle (the page language comes from here). Each page's
 * text lives in its own module beside this one, imported by its own route only; its sample session, in
 * `data/session-samples.json` under the page's id.
 */

export type LocalPageLang = 'en' | 'fr' | 'de' | 'es' | 'it' | 'pl' | 'ja' | 'zh'

export type LocalPageEntry = {
    path: string
    lang: LocalPageLang
    /** The one query this page owns: in its title, its H1 and its opening, on no other page. */
    ownerQuery: string
    /** The owner query as a link to the page reads, capitalised the way its language writes it. */
    anchor: string
    /** `meditation`: the trial of decision 5, read after eight weeks. */
    kind: 'method' | 'meditation'
}

export const LOCAL_PAGES = {
    sophrologie: {path: '/sophrologie', lang: 'fr', ownerQuery: 'sophrologie', anchor: 'Sophrologie', kind: 'method'},
    'yoga-nidra': {path: '/yoga-nidra', lang: 'en', ownerQuery: 'yoga nidra', anchor: 'Yoga nidra', kind: 'method'},
    'zi-wo-cui-mian': {path: '/zi-wo-cui-mian', lang: 'zh', ownerQuery: '自我催眠', anchor: '自我催眠', kind: 'method'},
    'autogenes-training': {
        path: '/autogenes-training',
        lang: 'de',
        ownerQuery: 'autogenes training',
        anchor: 'Autogenes Training',
        kind: 'method',
    },
    'progressive-muscle-relaxation': {
        path: '/progressive-muscle-relaxation',
        lang: 'en',
        ownerQuery: 'progressive muscle relaxation',
        anchor: 'Progressive muscle relaxation',
        kind: 'method',
    },
    'training-autogeno': {
        path: '/training-autogeno',
        lang: 'it',
        ownerQuery: 'training autogeno',
        anchor: 'Training autogeno',
        kind: 'method',
    },
    nsdr: {path: '/nsdr', lang: 'en', ownerQuery: 'nsdr', anchor: 'NSDR', kind: 'method'},
    'jiritsu-kunren-ho': {path: '/jiritsu-kunren-ho', lang: 'ja', ownerQuery: '自律訓練法', anchor: '自律訓練法', kind: 'method'},
    'trening-autogenny': {
        path: '/trening-autogenny',
        lang: 'pl',
        ownerQuery: 'trening autogenny',
        anchor: 'Trening autogenny',
        kind: 'method',
    },
    'relajacion-progresiva-de-jacobson': {
        path: '/relajacion-progresiva-de-jacobson',
        lang: 'es',
        ownerQuery: 'relajación progresiva',
        anchor: 'Relajación progresiva',
        kind: 'method',
    },
    'sleep-meditation': {
        path: '/sleep-meditation',
        lang: 'en',
        ownerQuery: 'sleep meditation',
        anchor: 'Sleep meditation',
        kind: 'meditation',
    },
    'meditation-for-anxiety': {
        path: '/meditation-for-anxiety',
        lang: 'en',
        ownerQuery: 'meditation for anxiety',
        anchor: 'Meditation for anxiety',
        kind: 'meditation',
    },
    'meditacion-guiada': {
        path: '/meditacion-guiada',
        lang: 'es',
        ownerQuery: 'meditación guiada',
        anchor: 'Meditación guiada',
        kind: 'meditation',
    },
} as const satisfies Record<string, LocalPageEntry>

export type LocalPageId = keyof typeof LOCAL_PAGES

/** A paragraph (inline links as `[anchor](/path)`), a list, or a pulled line. */
export type LocalPageBlock = string | {list: string[]} | {quote: string}

export type LocalPageContent = {
    id: LocalPageId
    meta: {title: string; description: string}
    h1: string
    /** The first lines under the H1. */
    opening: string
    /** The breadcrumb: the home this page belongs under, and its own short name. */
    home: {label: string; path: string}
    crumb: string
    sections: {title: string; blocks: LocalPageBlock[]}[]
    /** Same-language pages only. */
    related: {title: string; description: string; path: string}[]
    ctaHeadline: string
    datePublished: string
}
